/**
 * Organizations feature hooks — React Query wrappers.
 */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { organizationsApi } from './api'
import type { PagedRequest } from '@/shared/types'
import type { CreateOrganizationRequest, UpdateOrganizationRequest } from './types'

// ── Queries ───────────────────────────────────────────────────────────

export function useOrganizations(params?: PagedRequest) {
    return useQuery({
        queryKey: ['organizations', params],
        queryFn: async () => {
            const res = await organizationsApi.list(params)
            return res.data
        },
        placeholderData: keepPreviousData,
    })
}

export function useOrganization(id: string) {
    return useQuery({
        queryKey: ['organizations', id],
        queryFn: async () => {
            const res = await organizationsApi.get(id)
            return res.data
        },
        enabled: !!id,
    })
}

export function useOrganizationMembers(id: string) {
    return useQuery({
        queryKey: ['organizations', id, 'members'],
        queryFn: async () => {
            const res = await organizationsApi.members(id)
            return res.data
        },
        enabled: !!id,
    })
}

export function useOrganizationAssessments(id: string) {
    return useQuery({
        queryKey: ['organizations', id, 'assessments'],
        queryFn: async () => {
            const res = await organizationsApi.assessments(id)
            return res.data
        },
        enabled: !!id,
    })
}

// Admin
export function useAllOrganizations(params?: PagedRequest) {
    return useQuery({
        queryKey: ['organizations', 'all', params],
        queryFn: async () => {
            const res = await organizationsApi.list(params)
            return res.data
        },
    })
}

// ── Mutations ─────────────────────────────────────────────────────────

export function useCreateOrganization() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateOrganizationRequest) => organizationsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
        },
    })
}

export function useUpdateOrganization() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationRequest }) =>
            organizationsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
            queryClient.invalidateQueries({ queryKey: ['stats', 'maturity-overview'] })
        },
    })
}

export function useDeleteOrganization() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => organizationsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
        },
    })
}

export function useUpdateMemberRole() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: string }) =>
            organizationsApi.updateUserRole(orgId, userId, role),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: ['organizations', vars.orgId, 'members'] })
        },
    })
}

// Admin mutations
export function useAdminUpdateOrganization() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => organizationsApi.adminUpdate(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
        },
    })
}

export function useAdminDeleteOrganization() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => organizationsApi.adminDelete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
        },
    })
}
